import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import SEO from '@/components/SEO';
import { fetchGlossaryList } from '@/services/pseoService';

const BASE_URL = 'https://seniorthrive.com';

const categoryLabels: Record<string, string> = {
  clinical: 'Clinical & Assessment',
  'home-safety': 'Home Safety',
  caregiving: 'Caregiving',
  wellness: 'Wellness & Lifestyle',
};

const categoryOrder = ['clinical', 'home-safety', 'caregiving', 'wellness'];

const LearnIndex = () => {
  const { data: entries, isLoading } = useQuery({
    queryKey: ['glossary-list'],
    queryFn: fetchGlossaryList,
    staleTime: 15 * 60 * 1000,
  });

  const grouped = (entries || []).reduce<Record<string, typeof entries>>((acc, entry) => {
    const cat = entry.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat]!.push(entry);
    return acc;
  }, {});

  const breadcrumbs = [
    { name: 'Home', url: BASE_URL },
    { name: 'Learning Center', url: `${BASE_URL}/learn` },
  ];

  return (
    <>
      <SEO
        title="Learning Center: Senior Care Terms Explained in Plain Language"
        description="Clear, plain-language guides to senior care, home safety, and caregiving terms. From ADLs to grab bars, learn what matters and what to do about it."
        keywords="senior care glossary, caregiving terms, home safety guide, aging terminology, elder care definitions"
        url={`${BASE_URL}/learn`}
        canonical={`${BASE_URL}/learn`}
        breadcrumbs={breadcrumbs}
      />

      <div className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <header className="mb-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full dark:bg-blue-950/50 mb-6">
              <BookOpenIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">Learning Center</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Senior care and caregiving terms explained in plain language. What they mean, why they matter, and what to do about them.
            </p>
          </header>

          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : (
            categoryOrder
              .filter((cat) => grouped[cat]?.length)
              .map((cat) => (
                <section key={cat} className="mb-12">
                  <h2 className="text-2xl font-bold text-foreground mb-6 border-b pb-3">
                    {categoryLabels[cat] || cat}
                  </h2>
                  <div className="grid gap-3">
                    {grouped[cat]!.map((entry) => (
                      <Link
                        key={entry.slug}
                        to={`/learn/${entry.slug}`}
                        className="group flex items-start gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors">
                            {entry.term}
                          </h3>
                          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                            {entry.definition}
                          </p>
                        </div>
                        <Badge variant="outline" className="shrink-0 mt-1">
                          {categoryLabels[entry.category] || entry.category}
                        </Badge>
                      </Link>
                    ))}
                  </div>
                </section>
              ))
          )}
        </div>
      </div>
    </>
  );
};

export default LearnIndex;
